import core from 'frontcore/index'
import moment from 'moment-timezone'
import {
  DATE_FORMAT, DATE_SIMPLE_FORMAT, DATETIME_FORMAT, DAY_MONTH_STRING_SHORT_FORMAT, HOUR_STRING_FORMAT, ISO_DATE_FORMAT,
  ISO_DATETIME_FORMAT, MONTH_NUMBER_FORMAT, MONTH_STRING_SHORT_FORMAT, MONTH_YEAR_STRING_SHORT_FORMAT, MONTHS_OF_YEAR,
  NATURAL_DATE_FORMAT, NATURAL_DATE_FORMAT_MIN, NATURAL_DATE_FORMAT_SHORT, NATURAL_DATETIME_FORMAT,
  NATURAL_NAME_DATETIME_FORMAT, NATURAL_SHORT_WITH_HOUR, NATURAL_SHORT_WITH_HOUR_AND_YEAR, NATURAL_SHORTER_NAME_DATETIME_FORMAT,
  SECONDS_IN_HOUR, SECONDS_IN_MINUTE, SERVER_WEEK_DAYS_VALUES, UNAWARE_DATETIME_FORMAT, WEEK_NUMBER_FORMAT,
} from 'frontcore/utils/dates/constants'

function adjustDate (m, { day, month, week, year, startOf, endOf } = {}) {
  if (!moment.isMoment(m)) {
    throw new Error(`${m} is not a moment instance.`)
  }
  if (year !== void 0) { m.year(year) }
  if (month !== void 0) { m.month(month) }
  if (week !== void 0) { m.isoWeek(week) }
  if (day !== void 0) { m.date(day) }
  if (startOf !== void 0) { m.startOf(startOf) }
  if (endOf !== void 0) { m.endOf(endOf) }
}

function adjustDateOffset (m, {
  hourOffset,
  dayOffset,
  weekOffset,
  monthOffset,
  yearOffset,
} = {}) {
  if (!moment.isMoment(m)) {
    throw new Error(`${m} is not a moment instance.`)
  }
  const duration = moment.duration()
  if (yearOffset) { duration.add(yearOffset, 'y') }
  if (monthOffset) { duration.add(monthOffset, 'M') }
  if (weekOffset) { duration.add(weekOffset, 'w') }
  if (dayOffset) { duration.add(dayOffset, 'd') }
  if (hourOffset) { duration.add(hourOffset, 'h') }
  m.add(duration)
}

/**
 * @param firstDate {string|undefined}
 * @param secondDate {string|undefined}
 * @param precision {string}
 * @param absolute {boolean}
 * @returns {number}
 */
function dateDiff (firstDate, secondDate, precision = 'd', absolute = false) {
  const m1 = moment(firstDate).utc()
  const m2 = moment(secondDate).utc()
  return m1.diff(m2, precision, absolute)
}

/**
 *
 * @param options {{ format: string }}
 * @returns {string}
 */
function getFactoryCurrentTime ({ format = ISO_DATETIME_FORMAT } = {}) {
  return moment().tz(core.factoryTimezone).format(format)
}

function getMomentFactoryDateFromUTC (date) {
  if (date === null) { return null }
  return moment(date, 'YYYY-MM-DDTHH:mm:ssZ').tz(core.factoryTimezone)
}

/**
 * @param date {string | Date | null}
 * @param {{format?: string}}
 * @returns {string|null}
 */
function getFactoryDateFromUTC (date, { format = DATE_FORMAT } = {}) {
  if (date === null) { return null }
  return getMomentFactoryDateFromUTC(date).format(format)
}

function getFactoryUTCMoment (date, {
  day,
  month,
  week,
  year,
  hourOffset,
  dayOffset,
  weekOffset,
  monthOffset,
  yearOffset,
  startOf,
  endOf,
} = {}) {
  if (date === null) { return null }
  const m = moment.tz(date, core.factoryTimezone)
  adjustDate(m, { day, month, week, year, startOf, endOf })
  adjustDateOffset(m, { hourOffset, dayOffset, weekOffset, monthOffset, yearOffset })
  return m.utc()
}

function getFactoryUTCDate (date, dateInformation = {}) {
  const {
    format = ISO_DATETIME_FORMAT,
  } = dateInformation
  if (date === null) { return null }
  return getFactoryUTCMoment(date, dateInformation).format(format)
}


/**
 * @param date {string|Date}
 * @param {{day?: number, month?: number, year?: number, hourOffset?: number, dayOffset?: number, monthOffset?: number, yearOffset?: string, format?: string}}
 * @returns {string}
 */
function getFormattedDate (date, {
  day,
  month,
  week,
  year,
  hourOffset,
  dayOffset,
  monthOffset,
  yearOffset,
  format = DATE_FORMAT,
} = {}) {
  const m = date ? moment(date) : moment()
  adjustDate(m, { day, month, week, year })
  adjustDateOffset(m, { hourOffset, dayOffset, monthOffset, yearOffset })
  return m.format(format)
}

function getHoshinYear (months = 0) {
  return moment().subtract(months, 'months').year()
}

function getUTCNow () {
  return moment.tz(moment(), moment.tz.guess()).utc().format(ISO_DATETIME_FORMAT)
}

export function getTodayUTCBegin () {
  return getFactoryUTCDate(undefined, { startOf: 'd', format: ISO_DATETIME_FORMAT })
}

export function getTodayUTCEnd () {
  return getFactoryUTCDate(undefined, { endOf: 'd', format: ISO_DATETIME_FORMAT })
}

/**
 *
 * @param day {Date}
 * @returns {*}
 */
export function getDayUTCBegin (day) {
  return getFactoryUTCDate(day, { startOf: 'd', format: ISO_DATETIME_FORMAT })
}

/**
 *
 * @param day {Date}
 * @returns {*}
 */
export function getDayUTCEnding (day) {
  return getFactoryUTCDate(day, { endOf: 'd', format: ISO_DATETIME_FORMAT })
}

export function getYear (date) {
  return moment(date).tz(core.factoryTimezone).year()
}

function getMonth (date) {
  return moment(date).tz(core.factoryTimezone).month()
}

function getMonthName (month) {
  const monthFormatString = isNaN(month) ? MONTH_STRING_SHORT_FORMAT : MONTH_NUMBER_FORMAT
  return moment(month, monthFormatString).format(MONTH_STRING_SHORT_FORMAT)
}

function getMonthsAgoMonth (date, numberMonthsAgo) {
  const m = date ? moment(date) : moment()
  return m.subtract(numberMonthsAgo, 'months').tz(core.factoryTimezone).month()
}

function getMonthsAgoName (date, numberMonthsAgo) {
  return getMonthName(getMonthsAgoMonth(date, numberMonthsAgo) + 1)
}

function getMonthNames ({ short } = {}) {
  return short ? moment.monthsShort() : moment.months()
}

function getYearMonthNames (startMonth = 1) {
  const months = []
  for (let i = 1; i <= MONTHS_OF_YEAR; i++) {
    months.push(getMonthName(i))
  }
  const lastComponent = months.slice(0, startMonth - 1)
  const firstComponent = months.slice(startMonth - 1)
  return firstComponent.concat(lastComponent)
}

function getWeek (date) {
  return moment(date).tz(core.factoryTimezone).week()
}

function getIsoWeek (date) {
  return moment(date).tz(core.factoryTimezone).isoWeek()
}

function getDateByNumberOfMonth (month) {
  return moment().month(month)
}

function getDatesFromRange (dates, separator='~') {
  return dates.split(separator)
}

function isPresentOrFutureDate (date) {
  return date && date >= new Date(new Date().setHours(0, 0, 0, 0))
}

function isFutureDate (date) {
  return date && date > new Date(new Date().setHours(0, 0, 0, 0))
}

function isPresentOrPastDate (date) {
  return date && date <= new Date(new Date().setHours(0, 0, 0, 0))
}

function getFirstDayOfYearDate (year = getYear()) {
  return year + '-01-01'
}

function getFirstMondayOfYear (year = getYear()) {
  const startOfYear = moment(year, 'YYYY').tz(core.factoryTimezone).startOf('year')
  const firstDayOfWeek = startOfYear.day()
  const daysUntilMonday = (8 - firstDayOfWeek) % 7
  const firstMonday = startOfYear.clone().add(daysUntilMonday, 'days')
  return firstMonday.toDate()
}

function getLastDayOfYearDate (year = getYear()) {
  return year + '-12-31'
}

function getLastDaysDate (dayOffset = -7, date=undefined) {
  return {
    from: getFactoryUTCDate(date,{ dayOffset: dayOffset, startOf: 'day' }),
    to: getFactoryUTCDate(date, { endOf: 'day' }),
  }
}

function getLastWeeksDate (weekOffset = -3, date=undefined) {
  return {
    from: getFactoryUTCDate(date,{ weekOffset: weekOffset, startOf: 'isoWeek' }),
    to: getFactoryUTCDate(date, { endOf: 'isoWeek' }),
  }
}

function getDayOfTheMonth (date) {
  return moment(date).tz(core.factoryTimezone).date()
}

function getLastMonthsDates (monthOffset = -3, date=undefined) {
  return {
    from: getFactoryUTCDate(date,{ monthOffset: monthOffset, startOf: 'month' }),
    to: getFactoryUTCDate(date, { endOf: 'month' }),
  }
}

function getUTCDatetimeFromDate (date) {
  if (!date) { return null }
  return getFactoryUTCDate(date)
}

function dateIsBefore (date, factoryTimezone, dateToCompare, precision) {
  const momentDate = moment(date).tz(factoryTimezone ?? core.factoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(factoryTimezone ?? core.factoryTimezone)
  return momentDate.isBefore(momentDateToCompare, precision)
}

function dateIsSameOrBefore (date, factoryTimezone, dateToCompare, precision) {
  const momentDate = moment(date).tz(factoryTimezone ?? core.factoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(factoryTimezone ?? core.factoryTimezone)
  return momentDate.isSameOrBefore(momentDateToCompare, precision)
}

function dateIsAfter (date, factoryTimezone, dateToCompare, precision) {
  const momentDate = moment(date).tz(factoryTimezone ?? core.factoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(factoryTimezone ?? core.factoryTimezone)
  return momentDate.isAfter(momentDateToCompare, precision)
}

function dateIsAfterNow (date, precision) {
  const dateToCompare = getUTCNow()
  return dateIsAfter(date, dateToCompare, precision)
}

function dateSameOrAfter (date, factoryTimezone, dateToCompare, precision) {
  const momentDate = moment(date).tz(factoryTimezone ?? core.factoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(factoryTimezone ?? core.factoryTimezone)
  return momentDate.isSameOrAfter(momentDateToCompare, precision)
}

function datesAreEqual (date, factoryTimezone, dateToCompare, precision) {
  const momentDate = moment(date).tz(factoryTimezone ?? core.factoryTimezone)
  const momentDateToCompare = moment(dateToCompare).tz(factoryTimezone ?? core.factoryTimezone)
  return momentDate.isSame(momentDateToCompare, precision)
}

function getLastDayWeek (date, useIsoFormat = false) {
  return moment(date).tz(core.factoryTimezone).endOf(useIsoFormat ? 'isoWeek' : 'week').toDate()
}

function getFirstDayWeek (date, useIsoFormat = false) {
  return moment(date).tz(core.factoryTimezone).startOf(useIsoFormat ? 'isoWeek' : 'week').toDate()
}

function isDateInCurrentWeek (date) {
  return moment(date).isSame(moment(), 'week')
}

/**
 * @param {{date?: number, day?: number, month?: number, year?: number, hour?: number, minutes?: number, seconds?: number, timeZone?: string}}
 * @returns {Date}
 */
function newDate ({
  date,
  day,
  month,
  year,
  hour,
  minutes,
  seconds,
  timeZone,
} = {}) {
  const m = moment()
  m.tz(timeZone || core.factoryTimezone)
  if (seconds !== undefined) m.seconds(seconds)
  if (minutes !== undefined) m.minutes(minutes)
  if (hour !== undefined) m.hour(hour)
  if (year !== undefined) m.year(year)
  if (month !== undefined) m.month(month)
  if (date !== undefined) m.date(date)
  if (day !== undefined) m.day(day)

  return m.toDate()
}

/**
 *
 * @param shift {object}
 * @param day {Date}
 */
function getShiftBeginAndEndDatesWithTimeForDay (shift, day) {
  const begin = new Date(day)
  const end = new Date(day)

  if (shiftIsDayChanging(shift)) {
    if (shiftStartedYesterday({referenceDate: day, shift})) {
      begin.setDate(end.getDate() - 1)
    } else if (shiftEndsTomorrow({referenceDate: day, shift})) {
      end.setDate(end.getDate() + 1)
    }
  }

  const {shiftBegin, shiftEnd } = decomposeShiftTime(shift)
  begin.setHours(shiftBegin.hours, shiftBegin.minutes, shiftBegin.seconds)
  end.setHours(shiftEnd.hours, shiftEnd.minutes, shiftEnd.seconds)
  return {begin, end}
}

function shiftIsDayChanging (shift) {
  const {shiftBegin: {hours: shiftBeginHours}, shiftEnd: {hours: shiftEndHours}} = decomposeShiftTime(shift)
  return shiftEndHours < shiftBeginHours
}

function shiftStartedYesterday ({referenceDate, shift}) {
  const currentHour = new Date(referenceDate).getHours()
  const {shiftBegin: {hours: shiftBeginHours}, shiftEnd: {hours: shiftEndHours}} = decomposeShiftTime(shift)
  return  shiftIsDayChanging(shift) && currentHour < shiftBeginHours && currentHour < shiftEndHours
}

function shiftEndsTomorrow ({referenceDate, shift}) {
  const currentHour = new Date(referenceDate).getHours()
  const {shiftEnd} = decomposeShiftTime(shift)
  return currentHour > shiftEnd.hours
}

function decomposeShiftTime (shift) {
  const TIME_RE = /(\d{2}):(\d{2}):(\d{2})/
  const [, shiftBeginHours, shiftBeginMinutes, shiftBeginSeconds] = shift.begin_time.match(TIME_RE).map(time => parseInt(time))
  const [, shiftEndHours, shiftEndMinutes, shiftEndSeconds] = shift.end_time.match(TIME_RE).map(time => parseInt(time))
  return {
    shiftBegin: {
      hours: shiftBeginHours, minutes: shiftBeginMinutes, seconds: shiftBeginSeconds,
    },
    shiftEnd: {
      hours: shiftEndHours, minutes: shiftEndMinutes, seconds: shiftEndSeconds,
    },
  }
}

function getShiftBeginAndEndDatesForCurrentDatetime (shift) {
  const lastSecondOfCurrentDatetime = moment().set({hour:23, minute:59, second:59, millisecond:99})
  const {begin, end} = getShiftBeginAndEndDatesWithTimeForDay(shift, lastSecondOfCurrentDatetime)
  return {begin, end}
}
function parseDurationToTime (duration, timeUnit) {
  return moment.duration(duration).as(timeUnit)
}
function parseTimeToDuration (time, timeUnit) {
  return formatTimeInSec(moment.duration(time, timeUnit).asSeconds())
}
function formatTimeInSec (sec) {
  const sec_num = parseInt(sec, 10)
  let hours   = Math.floor(sec_num / SECONDS_IN_HOUR)
  let minutes = Math.floor((sec_num - (hours * SECONDS_IN_HOUR)) / SECONDS_IN_MINUTE)
  let seconds = sec_num - (hours * SECONDS_IN_HOUR) - (minutes * SECONDS_IN_MINUTE)

  if (hours   < 10) { hours   = "0"+hours }
  if (minutes < 10) { minutes = "0"+minutes }
  if (seconds < 10) { seconds = "0"+seconds }
  return hours+':'+minutes+':'+ seconds
}

function customFormatTime (sec) {
  const timeValueArray = formatTimeInSec(sec).split(':')
  return `${timeValueArray[0]} h ${timeValueArray[1]} min ${timeValueArray[2]} s`
}

function getWeekdayNameAbbreviations () {
  const weekdays = moment.weekdaysMin()
  weekdays.push(weekdays.shift())
  return weekdays.map((weekday, index) => {
    return {
      value: index,
      text: weekday,
    }
  })
}

function getWeekdayNames () {
  const weekdays = moment.weekdays()
  weekdays.push(weekdays.shift())
  return weekdays.map((weekday, index) => {
    return {
      value: index,
      text: weekday,
    }
  })
}

/**
 *
 * @param date {Date}
 * @returns {number}
 */
function getWeekDayNumberFromDate (date) {
  const jsNativeDateWeekDayNumberToServerDayNumber = {
    0: SERVER_WEEK_DAYS_VALUES.SUNDAY,
    1: SERVER_WEEK_DAYS_VALUES.MONDAY,
    2: SERVER_WEEK_DAYS_VALUES.TUESDAY,
    3: SERVER_WEEK_DAYS_VALUES.WEDNESDAY,
    4: SERVER_WEEK_DAYS_VALUES.THURSDAY,
    5: SERVER_WEEK_DAYS_VALUES.FRIDAY,
    6: SERVER_WEEK_DAYS_VALUES.SATURDAY,
  }

  return jsNativeDateWeekDayNumberToServerDayNumber[date.getDay()]
}

/**
 * ex:
 *  getDifferenceTimeFromNow(new Date(2007, 0, 29));           // 4 years ago
 *  getDifferenceTimeFromNow(new Date(2007, 0, 29), true);     // 4 years
 *
 * @param date
 * @param withoutSuffix {boolean}
 * @returns {string}
 */
function getDifferenceTimeFromNow (date, withoutSuffix = false) {
  return moment(date).fromNow(withoutSuffix)
}

export const TEMPORALITIES = Object.freeze({
  day: Object.freeze({
    value: 'day',
    frequency: 'daily',
    token_temporality: 'common.messages.day',
    token_frequency: 'common.messages.daily',
    getFromDate: (_, quantity) => getLastDaysDate((quantity - 1) * -1).from,
    getToDate: (_, quantity) => getLastDaysDate((quantity - 1) * -1).to,
  }),
  week: Object.freeze({
    value: 'week',
    frequency: 'weekly',
    token_temporality: 'common.messages.week',
    token_frequency: 'common.messages.weekly',
    getFromDate: (_, quantity) => getLastWeeksDate((quantity - 1) * -1).from,
    getToDate: (_, quantity) => getLastWeeksDate((quantity - 1) * -1).to,
  }),
  month: Object.freeze({
    value: 'month',
    frequency: 'monthly',
    token_temporality: 'common.messages.month',
    token_frequency: 'common.messages.monthly',
    getFromDate: (_, quantity) => getLastMonthsDates((quantity - 1) * -1).from,
    getToDate: (_, quantity) => getLastMonthsDates((quantity - 1) * -1).to,
  }),
  quarter: Object.freeze({
    value: 'quarter',
    frequency: 'quarterly',
    token_temporality: 'common.messages.quarter',
    token_frequency: 'common.messages.quarterly',
    getFromDate: () => {},
    getToDate: () => {},
  }),
  year: Object.freeze({
    value: 'year',
    frequency: 'yearly',
    token_temporality: 'common.messages.year',
    token_frequency: 'common.messages.yearly',
    getFromDate: (frequency, quantity) => getUTCDatetimeFromDate(getFirstDayOfYearDate(getYear() - (quantity - 1))),
    getToDate: () => getUTCDatetimeFromDate(getLastDayOfYearDate(getYear())),
  }),
})

/**
 * @param date {string|Date}
 * @returns {{date: number, month: number, year: number, hour: number, minutes: number, seconds: number, timeZone: string}}
 */
function destructureDate (date) {
  const m = moment(date)
  return {
    date: m.date(),
    month: m.month(),
    year: m.year(),
    hour: m.hours(),
    minutes: m.minutes(),
    seconds: m.seconds(),
    timeZone: m.tz(),
  }
}

/**
 * @returns {boolean}
 */
const isUserInFactoryTimezone = () => {
  return core.factoryTimezone === Intl.DateTimeFormat().resolvedOptions().timeZone
}

export default {
  customFormatTime,
  dateIsAfter,
  dateIsAfterNow,
  dateSameOrAfter,
  dateIsBefore,
  dateIsSameOrBefore,
  datesAreEqual,
  destructureDate,
  getWeek,
  getIsoWeek,
  getDateByNumberOfMonth,
  getDatesFromRange,
  getDayOfTheMonth,
  dateDiff,
  getFactoryCurrentTime,
  getFactoryDateFromUTC,
  newDate,
  getFactoryUTCDate,
  getFactoryUTCMoment,
  getFirstDayOfYearDate,
  getFirstDayWeek,
  getFirstMondayOfYear,
  getFormattedDate,
  getHoshinYear,
  getLastDaysDate,
  getLastDayOfYearDate,
  getLastDayWeek,
  getLastMonthsDates,
  getLastWeeksDate,
  getMonth,
  getMonthName,
  getMonthsAgoName,
  getMonthsAgoMonth,
  getMonthNames,
  getMomentFactoryDateFromUTC,
  getDayUTCBegin,
  getDayUTCEnding,
  getTodayUTCBegin,
  getTodayUTCEnd,
  getShiftBeginAndEndDatesWithTimeForDay,
  getUTCDatetimeFromDate,
  getUTCNow,
  getYear,
  getYearMonthNames,
  isDateInCurrentWeek,
  isPresentOrFutureDate,
  isFutureDate,
  isPresentOrPastDate,
  isUserInFactoryTimezone,
  getShiftBeginAndEndDatesForCurrentDatetime,
  parseDurationToTime,
  parseTimeToDuration,
  formatTimeInSec,
  getWeekdayNames,
  getWeekdayNameAbbreviations,
  getDifferenceTimeFromNow,
  getWeekDayNumberFromDate,
  DATETIME_FORMAT,
  DATE_FORMAT,
  DATE_SIMPLE_FORMAT,
  DAY_MONTH_STRING_SHORT_FORMAT,
  HOUR_STRING_FORMAT,
  ISO_DATETIME_FORMAT,
  ISO_DATE_FORMAT,
  MONTH_YEAR_STRING_SHORT_FORMAT,
  NATURAL_DATETIME_FORMAT,
  NATURAL_DATE_FORMAT,
  NATURAL_DATE_FORMAT_MIN,
  NATURAL_DATE_FORMAT_SHORT,
  NATURAL_NAME_DATETIME_FORMAT,
  NATURAL_SHORT_WITH_HOUR,
  NATURAL_SHORT_WITH_HOUR_AND_YEAR,
  NATURAL_SHORTER_NAME_DATETIME_FORMAT,
  TEMPORALITIES,
  UNAWARE_DATETIME_FORMAT,
  WEEK_NUMBER_FORMAT,
}
